/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useRef, useState } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  baseSize: number;
  alpha: number;
  pulsing: boolean;
  pulseSpeed: number;
}

export default function ParticleBackground() {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: 0, y: 0, active: false, radius: 180 });
  // Skip canvas particles on mobile / reduced-motion — major Safari main-thread cost.
  const [enabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return !window.matchMedia(
      '(max-width: 1023px), (pointer: coarse), (prefers-reduced-motion: reduce)',
    ).matches;
  });

  useEffect(() => {
    if (!enabled) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let particles: Particle[] = [];
    const maxParticles = 48;

    // Cap device pixel ratio at 2 to balance crispness with rendering workload
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const initCanvas = () => {
      const container = containerRef.current;
      if (!container || !canvas) return;

      const rect = container.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);

      // Re-initialize particles to spread nicely on resize
      createParticles(rect.width, rect.height);
    };

    const createParticles = (width: number, height: number) => {
      particles = [];
      for (let i = 0; i < maxParticles; i++) {
        const baseSize = Math.random() * 2 + 1;
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.35, // Smooth slow drifting velocity
          vy: (Math.random() - 0.5) * 0.35,
          size: baseSize,
          baseSize,
          alpha: Math.random() * 0.5 + 0.2,
          pulsing: Math.random() > 0.5,
          pulseSpeed: 0.005 + Math.random() * 0.01,
        });
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      initCanvas();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
      mouseRef.current.active = true;
    };

    const handleMouseLeave = () => {
      mouseRef.current.active = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        const rect = canvas.getBoundingClientRect();
        mouseRef.current.x = e.touches[0].clientX - rect.left;
        mouseRef.current.y = e.touches[0].clientY - rect.top;
        mouseRef.current.active = true;
      }
    };

    const handleTouchEnd = () => {
      mouseRef.current.active = false;
    };

    // Attach listeners to the parent container for more natural tracking
    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseleave', handleMouseLeave);
      container.addEventListener('touchmove', handleTouchMove, { passive: true });
      container.addEventListener('touchend', handleTouchEnd);
    }

    // Animation Loop
    const draw = () => {
      if (!canvas || !ctx) return;
      const width = canvas.width / dpr;
      const height = canvas.height / dpr;

      ctx.clearRect(0, 0, width, height);

      // Draw interactive mouse glow (Aura lens spotlight)
      if (mouseRef.current.active) {
        const mouseGlow = ctx.createRadialGradient(
          mouseRef.current.x,
          mouseRef.current.y,
          0,
          mouseRef.current.x,
          mouseRef.current.y,
          mouseRef.current.radius
        );
        // Using Koemori's signature cyan color #00F5D4 with ultra-low alpha for an exquisite modern touch
        mouseGlow.addColorStop(0, 'rgba(0, 245, 212, 0.08)');
        mouseGlow.addColorStop(1, 'rgba(0, 245, 212, 0)');
        ctx.fillStyle = mouseGlow;
        ctx.beginPath();
        ctx.arc(mouseRef.current.x, mouseRef.current.y, mouseRef.current.radius, 0, Math.PI * 2);
        ctx.fill();
      }

      // Draw & Update Particles
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        // Idle drift
        let currentVx = p.vx;
        let currentVy = p.vy;

        // Interaction with mouse (magnetic repulsion/attraction)
        if (mouseRef.current.active) {
          const dx = p.x - mouseRef.current.x;
          const dy = p.y - mouseRef.current.y;
          const dist = Math.hypot(dx, dy);

          if (dist < mouseRef.current.radius) {
            // Calculate repulsion vector strength depending on distance
            const force = (mouseRef.current.radius - dist) / mouseRef.current.radius;
            const angle = Math.atan2(dy, dx);

            // Gently nudge particles away
            const pushX = Math.cos(angle) * force * 1.2;
            const pushY = Math.sin(angle) * force * 1.2;

            p.x += pushX;
            p.y += pushY;
          }
        }

        p.x += currentVx;
        p.y += currentVy;

        // Wrap around boundaries elegantly with a tiny offset buffer
        const buffer = 10;
        if (p.x < -buffer) p.x = width + buffer;
        else if (p.x > width + buffer) p.x = -buffer;

        if (p.y < -buffer) p.y = height + buffer;
        else if (p.y > height + buffer) p.y = -buffer;

        // Pulse particle size & alpha for an organic, breathing "firefly" aura feel
        if (p.pulsing) {
          p.alpha += p.pulseSpeed;
          p.size = p.baseSize + Math.sin(p.alpha * 5) * 0.4;
          if (p.alpha > 0.8) p.pulsing = false;
        } else {
          p.alpha -= p.pulseSpeed;
          p.size = p.baseSize - Math.sin(p.alpha * 5) * 0.2;
          if (p.alpha < 0.2) p.pulsing = true;
        }

        // Render current particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, Math.max(0.5, p.size), 0, Math.PI * 2);
        // Signature cyan #00F5D4 colored particle with dynamic alpha
        ctx.fillStyle = `rgba(0, 245, 212, ${p.alpha * 0.75})`;
        ctx.fill();

        // Draw network connections (faint high-tech mesh lines)
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.hypot(dx, dy);

          // Connect particles if within proximity threshold
          const threshold = 110;
          if (dist < threshold) {
            const opacity = (1 - dist / threshold) * 0.12; // Exquisite faint connection line
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);

            // Faint gradient color from Cyan to Mint
            ctx.strokeStyle = `rgba(0, 245, 212, ${opacity})`;
            ctx.lineWidth = 0.55;
            ctx.stroke();
          }
        }
      }

      animationFrameId = requestAnimationFrame(draw);
    };

    draw();

    // Clean up
    return () => {
      cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseleave', handleMouseLeave);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 w-full h-full z-0 pointer-events-auto"
      style={{ mixBlendMode: 'screen' }}
    >
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}
